import React, { useState, useEffect, useRef } from 'react';
import { FaChevronLeft, FaChevronRight, FaPlay } from 'react-icons/fa';
import { motion, AnimatePresence, useReducedMotion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import PageBanner from '../../components/PageBanner';
import SuccessShowcase from './SuccessShowcase';
import IndustryCollab from './IndustryCollab';
import TrainersShowcase from './TrainersShowcase';
import placementsDefault from '../../data/placements/placements.json';
import { getPlacements } from '../../services/data.service';
import assetUrl, { mediaUrl } from '../../services/assetUrl';
import './placementsNew.css';

// ---- Motion presets -------------------------------------------------------
const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.1 } } };

const Reveal = ({ children, className = '', as: Tag = motion.div, ...rest }) => (
    <Tag className={className} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} {...rest}>
        {children}
    </Tag>
);

// Animated count-up for the stat band. Handles "150+", "20 LPA", "5.5 LPA".
const StatNumber = ({ value }) => {
    const match = String(value).match(/^([\d.]+)(.*)$/);
    const target = match ? parseFloat(match[1]) : 0;
    const suffix = match ? match[2] : String(value);
    const decimals = match && match[1].includes('.') ? 1 : 0;
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.6 });
    const count = useMotionValue(0);
    const rounded = useTransform(count, (latest) => latest.toFixed(decimals));
    const [display, setDisplay] = useState(decimals ? '0.0' : '0');
    useEffect(() => rounded.on('change', (v) => setDisplay(v)), [rounded]);
    useEffect(() => {
        if (!inView) return;
        const controls = animate(count, target, { duration: 1.6, ease: 'easeOut' });
        return controls.stop;
    }, [inView, target, count]);
    return (<span ref={ref}>{display}<span>{suffix}</span></span>);
};

const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
const getEmbedVideoId = (url) => (url || '').match(/embed\/([^?&]+)/)?.[1] || '';

// Render a string with "\n" as <br/> (e.g. stat labels).
const withBreaks = (text) => String(text || '').split('\n').map((line, i, a) => (
    <React.Fragment key={i}>{line}{i < a.length - 1 && <br />}</React.Fragment>
));

// Section heading: "Title <span>Highlight</span>"
const headTitle = (head = {}) => (<>{head.title} {head.highlight && <span>{head.highlight}</span>}</>);

// ---- Recruiter logos (bundled brand assets, not DB-managed) ---------------
const formatRecruiterName = (filename) => filename.replace(/\.webp$/i, '').replace(/-\d+$/, '').split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
const recruiterLogoContext = require.context('../../assets/images/logo-images/output_webp', false, /\.webp$/);
const allRecruiterLogos = recruiterLogoContext.keys().map((key) => {
    const filename = key.replace('./', '');
    return { src: recruiterLogoContext(key), name: formatRecruiterName(filename) };
});
// Split a flat logo list into two marquee rows.
const toRecruiterRows = (logos) => [logos.filter((_, i) => i % 2 === 0), logos.filter((_, i) => i % 2 === 1)];

// ---- Icon maps (content stores a key; the SVG lives here) ------------------
const VMG_ICONS = {
    vision: (<><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></>),
    mission: (<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"></path>),
    goal: (<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>),
};
const PILLAR_ICONS = {
    skill: (<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path>),
    career: (<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>),
    connect: (<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>),
    alumni: (<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path>),
};

// Company logo with a navy monogram fallback if the image is missing.
const CompanyLogo = ({ src, name }) => {
    const [failed, setFailed] = useState(false);
    if (failed || !src) return <span className="plx-tst__logo-fallback">{initials(name)}</span>;
    return <img src={assetUrl(src)} alt={name} loading="lazy" onError={() => setFailed(true)} />;
};

const SectionHead = ({ eyebrow, children, lead }) => (
    <div className="placements-section__head">
        <span className="placements-eyebrow">{eyebrow}</span>
        <h2 className="placements-section__title">{children}</h2>
        {lead && <p className="plx-lead">{lead}</p>}
    </div>
);

// AI-Integrated Classrooms — cinematic horizontal accordion. Hover/focus (or tap
// on mobile) expands a panel while the rest gracefully shrink. Cover-cropped so
// any uploaded image shape fits; one panel is always featured.
const AIClassroomAccordion = ({ images = [] }) => {
    const [active, setActive] = useState(0);
    return (
        <div className="plx-aicr__strip" role="list">
            {images.map((src, i) => (
                <button
                    type="button"
                    key={i}
                    role="listitem"
                    className={`plx-aicr__panel${i === active ? ' is-active' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onFocus={() => setActive(i)}
                    onClick={() => setActive(i)}
                    aria-label={`AI-integrated classroom photo ${i + 1}`}
                    aria-expanded={i === active}
                >
                    <img src={assetUrl(src)} alt={`AI-integrated classroom at NCET ${i + 1}`} loading="lazy" />
                    <span className="plx-aicr__scrim" aria-hidden="true" />
                    <span className="plx-aicr__index" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                </button>
            ))}
        </div>
    );
};

const Placements = () => {
    // Dynamic content — instant render from the bundled JSON, override from backend.
    const [content, setContent] = useState(placementsDefault);
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await getPlacements();
                if (active && res?.data?.data) setContent(res.data.data); // SERVICE + wrapped doc → res.data.data
            } catch (e) {
                // keep the bundled fallback content
            }
        })();
        return () => { active = false; };
    }, []);

    // ---- Testimonials state ----
    const companies = content.testimonialCompanies || [];
    const [activeCompany, setActiveCompany] = useState(0);
    const [activeStudent, setActiveStudent] = useState(0);
    const [isTestimonialPaused, setIsTestimonialPaused] = useState(false);
    // Facade: the heavy YouTube player only mounts after the user clicks play,
    // so nothing loads (no YouTube telemetry) until a testimonial is watched.
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const tstCompany = companies[activeCompany] || companies[0] || { company: '', students: [] };
    const tstStudent = tstCompany.students[activeStudent] || tstCompany.students[0] || { name: '', branch: '', embedUrl: '' };

    const selectCompany = (i) => { setActiveCompany(i); setActiveStudent(0); };
    const goToStudent = (i) => setActiveStudent(i);
    const prevStudent = () => setActiveStudent((p) => (p - 1 + tstCompany.students.length) % tstCompany.students.length);
    const nextStudent = () => setActiveStudent((p) => (p + 1) % tstCompany.students.length);

    // Reset to the poster/facade whenever the selected testimonial changes.
    useEffect(() => { setIsVideoPlaying(false); }, [activeCompany, activeStudent]);

    useEffect(() => {
        // Don't auto-rotate while hovering or while a video is actually playing.
        if (isTestimonialPaused || isVideoPlaying) return undefined;
        const list = (content.testimonialCompanies || [])[activeCompany];
        const total = list ? list.students.length : 0;
        if (!total) return undefined;
        const t = setInterval(() => setActiveStudent((p) => (p + 1) % total), 9000);
        return () => clearInterval(t);
    }, [isTestimonialPaused, isVideoPlaying, activeCompany, content]);

    // Testimonials motion (reduced-motion aware)
    const reduceMotion = useReducedMotion();
    const idContainer = { hidden: {}, show: { transition: { staggerChildren: reduceMotion ? 0 : 0.06, delayChildren: reduceMotion ? 0 : 0.05 } } };
    const idItem = reduceMotion
        ? { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.2 } } }
        : { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.42, ease: [0.22, 1, 0.36, 1] } } };
    const underlineVar = reduceMotion
        ? { hidden: { opacity: 0 }, show: { opacity: 1 } }
        : { hidden: { scaleX: 0 }, show: { scaleX: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.12 } } };

    // hero montage faces (placed students with a photo)
    const placedAvatars = Object.values(content.successStories || {})
        .flatMap((c) => (c.students || []).map((s) => ({ ...s, company: c.company })))
        .filter((s) => s.img)
        .slice(0, 8);

    // ---- Section shorthands ----
    const hero = content.hero || {};
    const stats = content.stats || [];
    const successHead = content.successHead || {};
    const recruitersHead = content.recruitersHead || {};
    const industry = content.industryCollab || {};
    const projectWeek = content.projectWeek || {};
    const projectStreet = content.projectStreet || {};
    const trainers = content.trainers || placementsDefault.trainers || {};
    const certifications = content.certifications || {};
    const aiClassroom = content.aiClassroom || placementsDefault.aiClassroom || {};
    // Merged "AI-Ready Infrastructure & Certifications" section (classroom photos +
    // certification photos shown together as one accordion). Falls back to the
    // legacy `certifications` + `aiClassroom` fields so existing content keeps
    // rendering until an admin saves the consolidated `aiInfra` object.
    const aiInfra = content.aiInfra || {
        eyebrow: certifications.eyebrow || 'AI-Ready by Design',
        title: 'AI-Ready Infrastructure &',
        highlight: 'Certifications',
        text: aiClassroom.text || certifications.text || 'From Claude-certified trainers to AI-integrated classrooms and globally recognised certifications — our campus is built for an AI-ready future.',
        certs: certifications.certs || [],
        images: [...(aiClassroom.images || []), ...(certifications.gallery || [])],
    };
    const testimonialHead = content.testimonialHead || {};
    const vmg = content.vmg || [];
    const about = content.about || {};
    const pillars = content.pillars || [];
    const placementTeam = content.placementTeam || [];
    const teamHead = content.teamHead || {};
    const queries = content.queries || {};
    const records = content.records || {};
    const officeMessage = content.officeMessage || {};
    const cta = content.cta || {};
    // Top Recruiters: use admin-uploaded logos when present, else the bundled set.
    const recruiterLogos = (content.recruiters && content.recruiters.length)
        ? content.recruiters.map((src) => ({ src: assetUrl(src), name: '' }))
        : allRecruiterLogos;
    const RECRUITER_ROWS = toRecruiterRows(recruiterLogos);
    const recruitersLead = String(recruitersHead.leadTemplate || '').replace('{count}', recruiterLogos.length);

    return (
        <div className="placements-page">
            <Header />

            {/* ---- Hero ---- */}
            <PageBanner
                title={hero.title}
                eyebrow={hero.eyebrow}
                subtitle={hero.subtitle}
                className="placements-page-banner"
                image={hero.bannerImage ? mediaUrl(hero.bannerImage) : undefined}
            >
                <div className="plx-hero-montage" aria-hidden="true">
                    <div className="plx-hero-montage__faces">
                        {placedAvatars.map((s, i) => (
                            <span className="plx-hero-montage__face" key={i}><img src={assetUrl(s.img)} alt="" loading="lazy" /></span>
                        ))}
                    </div>
                    <span className="plx-hero-montage__more"><strong>{hero.montageMore}</strong> {hero.montageMoreLabel}</span>
                </div>
            </PageBanner>

            {/* ---- Impact stats ---- */}
            <div className="container placements-statband-wrap">
                <div className="placements-statband">
                    {stats.map((stat, i) => (
                        <div key={i} className="placements-statband__item">
                            <div className="placements-statband__value"><StatNumber value={stat.value} /></div>
                            <div className="placements-statband__label">{withBreaks(stat.label)}</div>
                        </div>
                    ))}
                </div>
                <div className="placements-statband-caption"><span>{content.statbandCaption}</span></div>
            </div>

            <div className="container placements-content">

                {/* ---- Success Stories ---- */}
                <Reveal as={motion.section} className="placements-section placements-section--center placements-section--compact">
                    <SectionHead eyebrow={successHead.eyebrow} lead={successHead.lead}>{headTitle(successHead)}</SectionHead>
                    <SuccessShowcase data={content.successStories} />
                </Reveal>

                {/* ---- Top Recruiters ---- */}
                <Reveal as={motion.section} className="placements-section placements-section--center placements-recruiters-section">
                    <SectionHead eyebrow={recruitersHead.eyebrow} lead={recruitersLead}>{headTitle(recruitersHead)}</SectionHead>
                    <div className="placements-recruiters-v2">
                        <div className="placements-recruiters-v2__fade placements-recruiters-v2__fade--left" aria-hidden="true" />
                        <div className="placements-recruiters-v2__fade placements-recruiters-v2__fade--right" aria-hidden="true" />
                        {RECRUITER_ROWS.map((row, rowIdx) => (
                            <div key={rowIdx} className="placements-recruiters-v2__row">
                                <div className={`placements-recruiters-v2__track${rowIdx === 1 ? ' is-reverse' : ''}`}>
                                    {[...row, ...row].map((logo, i) => (
                                        <div key={`${logo.name}-${rowIdx}-${i}`} className="placements-recruiters-v2__logo" title={logo.name}>
                                            <img src={logo.src} alt={logo.name} loading="lazy" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </Reveal>

                {/* ---- Industry Collaboration gallery ---- */}
                <Reveal as={motion.section} className="placements-section placements-section--center plx-collab">
                    <SectionHead eyebrow={industry.eyebrow} lead={industry.text}>Industry <span>Collaboration</span></SectionHead>
                    <IndustryCollab data={industry} />
                </Reveal>

                {/* ---- Project Week ---- */}
                <Reveal as={motion.section} className="placements-section plx-pw">
                    <div className="plx-pw__panel">
                        <span className="plx-pw__bg" aria-hidden="true" />
                        <span className="plx-pw__orb" aria-hidden="true" />
                        <div className="plx-pw__head">
                            <span className="placements-eyebrow">{projectWeek.eyebrow}</span>
                            <h2 className="plx-pw__title">Project <span>Week</span></h2>
                            <p className="plx-pw__lead">{projectWeek.text}</p>
                        </div>
                        <motion.div className="plx-pw__steps" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}>
                            <span className="plx-pw__line" aria-hidden="true" />
                            {(projectWeek.steps || []).map((s) => (
                                <motion.div key={s.n} className="plx-pw__step" variants={fadeUp}>
                                    <span className="plx-pw__step-n">{s.n}</span>
                                    <h3>{s.title}</h3>
                                    <p>{s.text}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                        <motion.div className="plx-pw__gallery" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }}>
                            {(projectWeek.images || []).map((src, i) => (
                                <motion.figure key={i} className="plx-pw__shot" variants={fadeUp}>
                                    <img src={assetUrl(src)} alt={`Project Week ${i + 1}`} loading="lazy" />
                                </motion.figure>
                            ))}
                        </motion.div>
                    </div>
                </Reveal>

                {/* ---- Project Street ---- */}
                <Reveal as={motion.section} className="placements-section placements-section--center plx-street">
                    <SectionHead eyebrow={projectStreet.eyebrow} lead={projectStreet.text}>Project <span>Street</span></SectionHead>
                    <div className="plx-street__masonry">
                        {(projectStreet.images || []).map((src, i) => (
                            <figure key={i} className="plx-street__item"><img src={assetUrl(src)} alt={`Project Street ${i + 1}`} loading="lazy" /></figure>
                        ))}
                    </div>
                </Reveal>

                {/* ---- Meet Our Technical Trainers (Torii) ---- */}
                <TrainersShowcase trainers={trainers} />

                {/* ---- AI-Ready Infrastructure & Certifications (merged) ---- */}
                {(aiInfra.images || []).length > 0 && (
                    <Reveal as={motion.section} className="placements-section placements-section--center plx-aicr">
                        <SectionHead eyebrow={aiInfra.eyebrow} lead={aiInfra.text}>
                            {aiInfra.title} {aiInfra.highlight && <span>{aiInfra.highlight}</span>}
                        </SectionHead>
                        {(aiInfra.certs || []).filter(Boolean).length > 0 && (
                            <div className="plx-certs__chips">
                                {aiInfra.certs.filter(Boolean).map((c, i) => (<span key={i} className="plx-cert-chip">{c}</span>))}
                            </div>
                        )}
                        <AIClassroomAccordion images={aiInfra.images} />
                    </Reveal>
                )}

                {/* ---- Testimonials ---- */}
                <Reveal as={motion.section} className="placements-section placements-section--center plx-tst">
                    <SectionHead eyebrow={testimonialHead.eyebrow} lead={testimonialHead.lead}>{headTitle(testimonialHead)}</SectionHead>
                    <div className="plx-tst__stage" onMouseEnter={() => setIsTestimonialPaused(true)} onMouseLeave={() => setIsTestimonialPaused(false)}>
                        {/* LEFT — company selector (master) */}
                        <div className="plx-tst__companies" role="tablist" aria-label="Companies">
                            {companies.map((c, i) => {
                                const isActive = i === activeCompany;
                                return (
                                    <button key={c.company} type="button" role="tab" aria-selected={isActive} className={`plx-tst__company${isActive ? ' is-active' : ''}`} onClick={() => selectCompany(i)}>
                                        <span className="plx-tst__company-logo"><CompanyLogo src={c.logo} name={c.company} /></span>
                                        <span className="plx-tst__company-txt">
                                            <strong>{c.company}</strong>
                                            <span>{c.students.length} {c.students.length === 1 ? 'story' : 'stories'}</span>
                                        </span>
                                        <span className="plx-tst__company-caret" aria-hidden="true"><FaChevronRight /></span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* RIGHT — selected company's testimonials (detail) */}
                        <div className="plx-tst__detail">
                            <div className="plx-tst__viewer">
                                <div className="plx-tst__frame">
                                    <motion.div key={`${activeCompany}-${activeStudent}`} className="plx-tst__video" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: reduceMotion ? 0 : 0.4, ease: 'easeOut' }}>
                                        {isVideoPlaying ? (
                                            <iframe
                                                src={`https://www.youtube-nocookie.com/embed/${getEmbedVideoId(tstStudent.embedUrl)}?autoplay=1&rel=0`}
                                                title={`${tstStudent.name} — placement testimonial`}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                allowFullScreen
                                            />
                                        ) : (
                                            <button type="button" className="plx-tst__facade" onClick={() => setIsVideoPlaying(true)} aria-label={`Play ${tstStudent.name}'s testimonial`}>
                                                <img
                                                    src={`https://img.youtube.com/vi/${getEmbedVideoId(tstStudent.embedUrl)}/maxresdefault.jpg`}
                                                    onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${getEmbedVideoId(tstStudent.embedUrl)}/hqdefault.jpg`; }}
                                                    alt=""
                                                    loading="lazy"
                                                />
                                                <span className="plx-tst__facade-scrim" aria-hidden="true" />
                                                <span className="plx-tst__facade-play" aria-hidden="true"><FaPlay /></span>
                                            </button>
                                        )}
                                    </motion.div>
                                    {!reduceMotion && !isVideoPlaying && (
                                        <div className="plx-tst__progress" aria-hidden="true">
                                            <span key={`${activeCompany}-${activeStudent}`} className="plx-tst__progress-fill" style={{ animationPlayState: isTestimonialPaused ? 'paused' : 'running' }} />
                                        </div>
                                    )}
                                </div>

                                <AnimatePresence mode="wait">
                                    <motion.div key={`${activeCompany}-${activeStudent}-id`} className="plx-tst__caption" variants={idContainer} initial="hidden" animate="show" exit={{ opacity: 0, transition: { duration: 0.18 } }}>
                                        <motion.div className="plx-tst__cap-main" variants={idItem}>
                                            <h3 className="plx-tst__name">{tstStudent.name}<motion.span className="plx-tst__underline" variants={underlineVar} aria-hidden="true" /></h3>
                                            <span className="plx-tst__placed"><span className="plx-tst__placed-label">Placed at</span> <span className="plx-tst__company-name">{tstCompany.company}</span></span>
                                        </motion.div>
                                        <motion.span className="plx-tst__pill" variants={idItem}>{tstStudent.branch}</motion.span>
                                    </motion.div>
                                </AnimatePresence>
                            </div>

                            {/* student thumbnail rail for the selected company */}
                            <div className="plx-tst__rail-row">
                                <button type="button" className="plx-tst__arrow" onClick={prevStudent} aria-label="Previous student"><FaChevronLeft /></button>
                                <ul className="plx-tst__rail" role="tablist" aria-label={`${tstCompany.company} testimonials`}>
                                    {tstCompany.students.map((s, i) => {
                                        const videoId = getEmbedVideoId(s.embedUrl);
                                        const isActive = i === activeStudent;
                                        return (
                                            <li key={s.name} role="presentation">
                                                <button type="button" role="tab" aria-selected={isActive} aria-label={`${s.name}, ${s.branch}`} className={`plx-tst__thumb${isActive ? ' is-active' : ''}`} onClick={() => goToStudent(i)}>
                                                    <img src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`} alt="" loading="lazy" />
                                                    {!isActive && <span className="plx-tst__thumb-play" aria-hidden="true"><FaPlay /></span>}
                                                    <span className="plx-tst__thumb-name">{s.name}</span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <button type="button" className="plx-tst__arrow" onClick={nextStudent} aria-label="Next student"><FaChevronRight /></button>
                                <span className="plx-tst__counter">{String(activeStudent + 1).padStart(2, '0')}<small> / {String(tstCompany.students.length).padStart(2, '0')}</small></span>
                            </div>
                        </div>
                    </div>
                </Reveal>

                {/* ---- Vision / Mission / Goal ---- */}
                <motion.section className="placements-section placements-vmg" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
                    {vmg.map((card, i) => (
                        <motion.div key={i} className="placements-vmg__card" variants={fadeUp}>
                            <div className="placements-vmg__icon"><svg fill="none" viewBox="0 0 24 24" stroke="currentColor">{VMG_ICONS[card.key]}</svg></div>
                            <h3 className="placements-vmg__title">{card.title}</h3>
                            <p className="placements-vmg__text">{card.text}</p>
                        </motion.div>
                    ))}
                </motion.section>

                {/* ---- About Placements + Support pillars ---- */}
                <Reveal as={motion.section} className="placements-section">
                    <div className="placements-about">
                        <span className="placements-eyebrow">{about.eyebrow}</span>
                        <h2 className="placements-section__title">{headTitle(about)}</h2>
                        <p className="placements-about__desc">{about.desc}</p>
                    </div>
                    <motion.div className="placements-pillars" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }}>
                        {pillars.map((pillar, i) => (
                            <motion.article key={i} className="placements-pillar" variants={fadeUp}>
                                <span className="placements-pillar__num">{pillar.num}</span>
                                <div className="placements-pillar__head">
                                    <div className="placements-pillar__icon"><svg fill="none" viewBox="0 0 24 24" stroke="currentColor">{PILLAR_ICONS[pillar.key]}</svg></div>
                                    <h3 className="placements-pillar__title">{pillar.title}</h3>
                                </div>
                                <ul className="placements-pillar__list">
                                    {(pillar.items || []).map((item, j) => (<li key={j} className="placements-pillar__item"><span className="placements-pillar__dot"></span>{item}</li>))}
                                </ul>
                            </motion.article>
                        ))}
                    </motion.div>
                </Reveal>

                {/* ---- Placement Team ---- */}
                <Reveal as={motion.section} className="placements-section placements-section--center plx-team">
                    <SectionHead eyebrow={teamHead.eyebrow}>{headTitle(teamHead)}</SectionHead>
                    <motion.div className="plx-team__grid" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.12 }}>
                        {placementTeam.map((m, i) => (
                            <motion.article key={i} className="plx-team__card" variants={fadeUp}>
                                <div className="plx-team__avatar">{m.photo ? <img src={assetUrl(m.photo)} alt={m.name} loading="lazy" /> : <span>{initials(m.name)}</span>}</div>
                                <h3 className="plx-team__name">{m.name}</h3>
                                <p className="plx-team__role">{m.role}</p>
                            </motion.article>
                        ))}
                    </motion.div>
                </Reveal>

                {/* ---- Contact + Placement Records ---- */}
                <Reveal as={motion.section} className="placements-section placements-section--compact placements-hub-v2">
                    <div className="placements-hub-v2__grid">
                        <div className="placements-queries-v2">
                            <span className="placements-eyebrow">{queries.eyebrow}</span>
                            <h2 className="placements-section__title">{headTitle(queries)}</h2>
                            <p className="placements-queries-v2__intro">{queries.intro}</p>
                            <div className="placements-queries-v2__list">
                                <a href={`tel:${queries.phone}`} className="placements-query-item">
                                    <span className="placements-query-item__icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg></span>
                                    <span className="placements-query-item__meta"><span className="label">Phone</span><span className="value">{queries.phone}</span></span>
                                </a>
                                <a href={`mailto:${queries.email}`} className="placements-query-item">
                                    <span className="placements-query-item__icon placements-query-item__icon--mail"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg></span>
                                    <span className="placements-query-item__meta"><span className="label">Email</span><span className="value">{queries.email}</span></span>
                                </a>
                            </div>
                        </div>
                        <div className="placements-records-v2">
                            <div className="placements-records-v2__head">
                                <span className="placements-eyebrow">{records.head?.eyebrow}</span>
                                <h2 className="placements-section__title">{headTitle(records.head || {})}</h2>
                            </div>
                            <motion.div className="placements-records-v2__list" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
                                {(records.years || []).map((year) => (
                                    <motion.a key={year} href={`${records.pdfBase || ''}${year.replace('-', '_')}.pdf`} target="_blank" rel="noopener noreferrer" className="placements-record-row" variants={fadeUp}>
                                        <div className="placements-record-row__left"><span className="year">{year}</span><span className="subtitle">Placement Report PDF</span></div>
                                        <span className="placements-record-row__action">Download</span>
                                    </motion.a>
                                ))}
                            </motion.div>
                        </div>
                    </div>
                </Reveal>

                {/* ---- Message from the Office of Placements ---- */}
                <Reveal as={motion.section} className="placements-section placements-section--compact placements-office-msg">
                    <div className="placements-office-msg__shell">
                        <article className="placements-office-msg__letter">
                            <header className="placements-office-msg__head">
                                <span className="placements-eyebrow">{officeMessage.eyebrow}</span>
                                <h2>{officeMessage.heading}</h2>
                                <p className="placements-office-msg__institution">{officeMessage.institution}</p>
                            </header>
                            <blockquote className="placements-office-msg__lead">
                                <span className="placements-office-msg__quote-mark" aria-hidden="true">&ldquo;</span>
                                <p>{(officeMessage.paragraphs || [])[0]}</p>
                            </blockquote>
                            <div className="placements-office-msg__body">
                                {(officeMessage.paragraphs || []).slice(1).map((para, i) => (<p key={i}>{para}</p>))}
                            </div>
                            <div className="placements-office-msg__assurance">
                                <span className="placements-office-msg__assurance-badge">Our Assurance</span>
                                <p>{officeMessage.closing}</p>
                            </div>
                        </article>
                        <aside className="placements-office-msg__aside" aria-label="Placement office highlights">
                            <div className="placements-office-msg__panel">
                                <div className="placements-office-msg__panel-head">
                                    <span className="placements-office-msg__panel-eyebrow">{officeMessage.panel?.eyebrow}</span>
                                    <h3>{officeMessage.panel?.heading}</h3>
                                    <p>{officeMessage.panel?.text}</p>
                                </div>
                                <ul className="placements-office-msg__highlights">
                                    {(officeMessage.highlights || []).map((item, i) => (
                                        <li key={item.title}>
                                            <span className="placements-office-msg__highlight-index">{String(i + 1).padStart(2, '0')}</span>
                                            <div><strong>{item.title}</strong><p>{item.text}</p></div>
                                        </li>
                                    ))}
                                </ul>
                                <div className="placements-office-msg__stats">
                                    {stats.slice(0, 3).map((stat) => (<div key={stat.value} className="placements-office-msg__stat"><strong>{stat.value}</strong><span>{withBreaks(stat.label)}</span></div>))}
                                </div>
                            </div>
                        </aside>
                    </div>
                </Reveal>

                {/* ---- CTA ---- */}
                <Reveal as={motion.section} className="placements-section placements-cta">
                    <div className="placements-cta__glow placements-cta__glow--1"></div>
                    <div className="placements-cta__glow placements-cta__glow--2"></div>
                    <div className="placements-cta__inner">
                        <div className="placements-cta__copy">
                            <h2 className="placements-cta__title">{cta.title} <span>{cta.highlight}</span></h2>
                            <p className="placements-cta__text">{cta.text}</p>
                        </div>
                        <a href={cta.buttonLink} target="_blank" rel="noopener noreferrer" className="placements-cta__btn">
                            {cta.buttonLabel}
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                        </a>
                    </div>
                </Reveal>
            </div>

            <Footer />
        </div>
    );
};

export default Placements;
